import React, { useState, useRef, useEffect } from 'react';
import { useField } from 'formik';

const SimpleHTMLEditor = ({
    name,
    label,
    placeholder = "Enter content...",
    required = false,
    height = 500,
    ...props
}) => {
    const [field, meta, helpers] = useField(name);
    const [isPreview, setIsPreview] = useState(false);
    const textareaRef = useRef(null);

    const handleChange = (e) => {
        helpers.setValue(e.target.value);
        helpers.setTouched(true);
    };

    const handleBlur = () => {
        helpers.setTouched(true);
    };

    const togglePreview = () => {
        setIsPreview(!isPreview);
    };

    const insertTag = (tag) => {
        const textarea = textareaRef.current;
        if (textarea) {
            const start = textarea.selectionStart;
            const end = textarea.selectionEnd;
            const selectedText = field.value.substring(start, end);
            const newText = field.value.substring(0, start) + tag.replace('{}', selectedText) + field.value.substring(end);
            helpers.setValue(newText);

            // Set cursor position after the inserted tag
            setTimeout(() => {
                textarea.focus();
                textarea.setSelectionRange(start + tag.indexOf('{}') + selectedText.length, start + tag.indexOf('{}') + selectedText.length);
            }, 0);
        }
    };

    const toolbarButtons = [
        { label: 'Bold', tag: '<strong>{}</strong>' },
        { label: 'Italic', tag: '<em>{}</em>' },
        { label: 'Underline', tag: '<u>{}</u>' },
        { label: 'H1', tag: '<h1>{}</h1>' },
        { label: 'H2', tag: '<h2>{}</h2>' },
        { label: 'H3', tag: '<h3>{}</h3>' },
        { label: 'Paragraph', tag: '<p>{}</p>' },
        { label: 'Link', tag: '<a href="url">{}</a>' },
        { label: 'Image', tag: '<img src="url" alt="description" />' },
        { label: 'Line Break', tag: '<br />' },
        { label: 'Quote', tag: '<blockquote>{}</blockquote>' },
        { label: 'Code', tag: '<code>{}</code>' },
    ];

    return (
        <div className="form-control w-full">
            {/* Label */}
            {label && (
                <label
                    htmlFor={name}
                    className="block text-sm font-medium text-gray-700 mb-1"
                >
                    {label}
                    {required && <span className="text-red-500 ml-1">*</span>}
                </label>
            )}

            {/* Toolbar */}
            <div className="border border-gray-300 rounded-t-lg bg-gray-50 p-2">
                <div className="flex flex-wrap gap-1">
                    {toolbarButtons.map((button, index) => (
                        <button
                            key={index}
                            type="button"
                            onClick={() => insertTag(button.tag)}
                            className="px-2 py-1 text-xs bg-white border border-gray-300 rounded hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary/50"
                            title={button.label}
                        >
                            {button.label}
                        </button>
                    ))}
                    <button
                        type="button"
                        onClick={togglePreview}
                        className={`px-2 py-1 text-xs border rounded hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary/50 ${isPreview
                            ? 'bg-primary text-white border-primary'
                            : 'bg-white border-gray-300'
                            }`}
                        title="Toggle Preview"
                    >
                        {isPreview ? 'Edit' : 'Preview'}
                    </button>
                </div>
            </div>

            {/* Editor/Preview */}
            <div className={`border border-gray-300 rounded-b-lg transition-all duration-200 ${meta.touched && meta.error
                ? 'border-red-500 focus-within:border-red-500 focus-within:ring-2 focus-within:ring-red-500/30'
                : 'border-gray-300 hover:border-gray-400 focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/50'
                }`}>
                {isPreview ? (
                    <div
                        className="p-3 min-h-48 prose prose-sm max-w-none"
                        style={{ height: `${height}px`, overflowY: 'auto' }}
                        dangerouslySetInnerHTML={{ __html: field.value || '<p>No content to preview</p>' }}
                    />
                ) : (
                    <textarea
                        ref={textareaRef}
                        name={name}
                        value={field.value || ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder={placeholder}
                        className="w-full p-3 border-0 resize-none focus:outline-none"
                        style={{
                            height: `${height}px`,
                            fontFamily: 'monospace'
                        }}
                        {...props}
                    />
                )}
            </div>

            {/* Error Message */}
            {meta.touched && meta.error && (
                <p
                    id={`${name}-error`}
                    className="mt-1 text-sm text-red-600 flex items-center gap-1"
                    role="alert"
                >
                    <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {meta.error}
                </p>
            )}

            {/* Character and Word Count */}
            <div className="mt-1 text-xs text-gray-500 text-right">
                {(() => {
                    const text = field.value ? field.value.replace(/<[^>]*>/g, '') : '';
                    const words = text.trim() ? text.trim().split(/\s+/).length : 0;
                    return `${text.length} characters, ${words} words`;
                })()}
            </div>
        </div>
    );
};

export default SimpleHTMLEditor;
